import { useState, useMemo } from 'react';
import CommonModal from '../common/Modal';
import { useFirestore, useFirestoreOrderBy } from '../../firebase/useFirestore';

/**
 * Portfolio→project: pick effects to request as synchronisations. MSI
 * ("Meetstandaard") effects sync as a whole; non-MSI effects expand to a list
 * of their questions, each picked individually. The modal owns its selection;
 * on confirm it hands the picked docs to `onConfirm`.
 *
 * @param {object} props
 * @param {boolean} props.modalOpen - Whether the modal is shown.
 * @param {(open: boolean) => void} props.setModalOpen - Toggles the modal.
 * @param {(selection: { effects: object[], questions: object[] }) => void} props.onConfirm
 *   Called with the picked MSI OutputEffects docs and QuestionnaireFields docs.
 */
const AddEffectSyncsModal = ({ modalOpen, setModalOpen, onConfirm }) => {
    const effects = useFirestoreOrderBy('OutputEffects', 'Position', 'asc');
    const questionFields = useFirestore('QuestionnaireFields');

    const [expandedEffectId, setExpandedEffectId] = useState('');
    const [selectedEffectIds, setSelectedEffectIds] = useState(new Set());
    const [selectedQuestionIds, setSelectedQuestionIds] = useState(new Set());

    const effectList = useMemo(
        () => (Array.isArray(effects) ? effects : []),
        [effects]
    );

    // EffectId is an array on QuestionnaireFields — a field can measure several
    // effects, so it lands under each.
    const questionsByEffect = useMemo(() => {
        const map = {};
        (Array.isArray(questionFields) ? questionFields : []).forEach((field) => {
            if (field.Type === 'section') return;
            (field.EffectId || []).forEach((effectId) => {
                (map[effectId] ||= []).push(field);
            });
        });
        return map;
    }, [questionFields]);

    const toggleId = (setFn) => (id) => {
        setFn((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleEffect = toggleId(setSelectedEffectIds);
    const toggleQuestion = toggleId(setSelectedQuestionIds);

    const resetState = () => {
        setExpandedEffectId('');
        setSelectedEffectIds(new Set());
        setSelectedQuestionIds(new Set());
    };

    const closeModal = () => {
        resetState();
        setModalOpen(false);
    };

    const handleConfirm = () => {
        const pickedEffects = effectList.filter((effect) =>
            selectedEffectIds.has(effect.ID)
        );
        const pickedQuestions = (Array.isArray(questionFields) ? questionFields : []).filter(
            (field) => selectedQuestionIds.has(field.ID)
        );
        onConfirm({ effects: pickedEffects, questions: pickedQuestions });
        closeModal();
    };

    const totalSelected = selectedEffectIds.size + selectedQuestionIds.size;

    const renderMsiEffectRow = (effect) => (
        <div key={effect.ID} className="subscribe-modal__item">
            <label className="subscribe-modal__checkbox-row">
                <input
                    type="checkbox"
                    checked={selectedEffectIds.has(effect.ID)}
                    onChange={() => toggleEffect(effect.ID)}
                />
                <span>
                    {effect.Effect || '(zonder titel)'}{' '}
                    <span className="subscribe-modal__item-meta">(Meetstandaard)</span>
                </span>
            </label>
        </div>
    );

    const renderEffectRow = (effect) => {
        const effectQuestions = questionsByEffect[effect.ID] || [];
        const isExpanded = expandedEffectId === effect.ID;

        return (
            <div key={effect.ID} className="subscribe-modal__item">
                <div className="subscribe-modal__item-header">
                    <p className="subscribe-modal__item-title">
                        {effect.Effect || '(zonder titel)'}{' '}
                        <span className="subscribe-modal__item-meta">
                            ({effectQuestions.length} vragen)
                        </span>
                    </p>
                    <button
                        onClick={() =>
                            setExpandedEffectId(isExpanded ? '' : effect.ID)
                        }
                    >
                        {isExpanded ? 'Inklappen' : 'Vragen'}
                    </button>
                </div>

                {isExpanded && (
                    <div className="subscribe-modal__drilldown">
                        {effectQuestions.length === 0 ? (
                            <p className="subscribe-modal__empty">
                                Geen vragen gekoppeld aan dit effect.
                            </p>
                        ) : (
                            <div className="subscribe-modal__checkbox-list">
                                {effectQuestions.map((question) => (
                                    <label key={question.ID} className="subscribe-modal__checkbox-row">
                                        <input
                                            type="checkbox"
                                            checked={selectedQuestionIds.has(question.ID)}
                                            onChange={() => toggleQuestion(question.ID)}
                                        />
                                        <span>{question.Question || '(zonder titel)'}</span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <CommonModal
            modalOpen={modalOpen}
            setModalOpen={(value) => {
                if (!value) closeModal();
                else setModalOpen(true);
            }}
            pageHeader="Effecten koppelen"
            description="Meetstandaard-effecten koppel je in hun geheel; andere effecten per vraag."
            confirm={handleConfirm}
            confirmDisabled={totalSelected === 0}
            confirmLabel="Koppelingen aanvragen"
            content={
                <div className="subscribe-modal">
                    {effectList.length === 0 ? (
                        <p className="subscribe-modal__empty">Geen effecten gevonden.</p>
                    ) : (
                        <div className="subscribe-modal__list">
                            {effectList.map((effect) =>
                                effect.Standard === 'true'
                                    ? renderMsiEffectRow(effect)
                                    : renderEffectRow(effect)
                            )}
                        </div>
                    )}
                </div>
            }
        />
    );
};

export default AddEffectSyncsModal;
